import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { updateStudent as updateStudentInSupabase, getStudentPaymentsById } from '../utils/studentsService';

// The `students` table has no `priceAmount` column — only the raw `price`
// string (e.g. "$25/h"). StudentProfile.js parses the number out of it when
// building its own studentData; mirror that here so this hook works whether
// studentData already has `priceAmount` attached or is the raw Supabase row.
function parsePriceAmount(priceString) {
  if (!priceString || typeof priceString !== 'string') return '';
  const m = priceString.match(/(\d+(?:\.\d+)?)/);
  return m ? m[1] : '';
}

/**
 * Shared payment-balance ledger + auto-charge reconciliation, backed by a
 * single React Query cache entry per student instead of a hand-rolled mix
 * of localStorage + refs + manual "is this stale" flags. That mix was the
 * root cause of three separate incidents in one evening: a deposit
 * reverting after navigating away and back, an auto-charge silently
 * overwriting a fresh deposit with a stale base, and the same overwrite
 * happening again on remount before a "fresh fetch" effect could resolve.
 * React Query's cache is the single place `payments` lives; a write updates
 * it immediately (queryClient.setQueryData) and cancels any in-flight fetch
 * for the same key first, so a slow, now-stale response can never land
 * after a newer local write and clobber it — no manual dirty-ref needed.
 *
 * Both the teacher's StudentProfile and the student's own Home view read
 * this same ledger, reconciled against every lesson whose status is 'done'
 * (adding a charge if one's missing, removing one if the lesson is no
 * longer 'done'), then synced to Supabase's students.payments column.
 * Whichever side loads most recently self-heals it.
 */
export function usePaymentBalance({ studentId, studentSupabaseId, studentData, lessons, scheduleItems, ready, useLocalCache = true }) {
  // paymentsKey doubles as "this is a real, saved student" guard across the
  // hook, independent of whether localStorage caching is actually enabled —
  // don't null it out for useLocalCache:false, gate each actual
  // localStorage read/write below on useLocalCache instead.
  const paymentsKey = studentId && studentId !== 'new' ? `novas_payments_${studentId}` : null;
  const hasSupabaseId = !!studentSupabaseId && studentSupabaseId !== 'new';

  const paymentCurrency = studentData?.priceCurrency || studentData?.price_currency || '$';
  const priceAmountRaw = studentData?.priceAmount ?? parsePriceAmount(studentData?.price);
  const priceAmount = parseFloat(priceAmountRaw) || 0;

  // priceAmount is interpreted as price per hour (matches Home's "money
  // this month" aggregation): lessonValue = pricePerHour * durationHours.
  const computeLessonCharge = (lesson, pricePerHour) => {
    let durationHours = 1;
    const from = lesson?.timeFrom || lesson?.from || lesson?.time_from;
    const to = lesson?.timeTo || lesson?.to || lesson?.time_to;
    if (from && to) {
      const fp = String(from).split(':');
      const tp = String(to).split(':');
      const fMin = (parseInt(fp[0], 10) || 0) * 60 + (parseInt(fp[1], 10) || 0);
      const tMin = (parseInt(tp[0], 10) || 0) * 60 + (parseInt(tp[1], 10) || 0);
      const mins = tMin - fMin;
      if (mins > 0) durationHours = Math.max(0.5, mins / 60);
    }
    return pricePerHour * durationHours;
  };

  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['studentPayments', studentSupabaseId ?? null, paymentsKey],
    [studentSupabaseId, paymentsKey]
  );

  // Only meaningful for useLocalCache:true (the student's own views) — an
  // instant paint from whatever's already known while the real value
  // loads. useLocalCache:false (the teacher's StudentProfile) never uses
  // this: showing a possibly-stale value there is exactly the bug this
  // hook used to have, so that mode waits for the real fetch instead (see
  // paymentsLoading below).
  const localFallback = () => {
    if (!paymentsKey) return [];
    try {
      const stored = localStorage.getItem(paymentsKey);
      if (stored) return JSON.parse(stored);
    } catch { /* fall through to studentData below */ }
    return Array.isArray(studentData?.payments) ? studentData.payments : [];
  };

  const { data: payments = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getStudentPaymentsById(studentSupabaseId).then(v => Array.isArray(v) ? v : []),
    enabled: !!paymentsKey && hasSupabaseId,
    ...(useLocalCache
      ? { placeholderData: localFallback, staleTime: 5 * 60 * 1000 }
      : { staleTime: 0 }),
  });

  const savePayments = (list) => {
    if (!paymentsKey) return;
    if (useLocalCache) {
      try { localStorage.setItem(paymentsKey, JSON.stringify(list)); } catch { /* best effort */ }
    }
    // Cancel any in-flight fetch for this key first — otherwise, if one was
    // already in flight when this write happens, its (now stale) response
    // would land after setQueryData below and silently overwrite it.
    queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData(queryKey, list);
    if (hasSupabaseId) {
      updateStudentInSupabase(studentSupabaseId, { payments: list }).catch(e =>
        console.error('Error saving payments to Supabase:', e)
      );
    }
  };

  // Reconcile auto-charges against current lesson statuses — adds a charge
  // for any 'done' item that doesn't have one yet, removes one whose item
  // we have data on but is no longer 'done'. Never touches charges whose
  // lessonId isn't in the known set (could belong to a deleted lesson).
  const notSettled = useLocalCache ? false : isLoading;
  useEffect(() => {
    if (!paymentsKey || !ready || notSettled) return;
    if (priceAmount <= 0) return;

    // Charging is driven ONLY by the schedule's own status — marking a
    // lesson "done" from the Lessons tab must never trigger a charge, only
    // marking the corresponding item "done" in the schedule does. `lessons`
    // is still consulted, but purely to fill in gaps (e.g. a missing
    // title) on a schedule item that shares its id, never to contribute
    // its own status or to add items that only exist in `lessons` with no
    // matching schedule entry.
    const lessonsById = new Map();
    (lessons || []).forEach(l => lessonsById.set(l.id, l));
    const items = (scheduleItems || []).map(s => {
      const lesson = lessonsById.get(s.id);
      return lesson ? { ...lesson, ...s, title: s.title || lesson.title } : s;
    });
    if (items.length === 0) return;

    const knownIds = new Set(items.map(i => i.id));
    const doneIds = new Set(items.filter(i => i.status === 'done').map(i => i.id));

    // Read straight from the query cache, not the `payments` closure —
    // this effect's deps deliberately exclude `payments` (it would refire
    // on every write, including its own), so the closure value here can
    // lag a render behind. getQueryData always returns the live value.
    const current = queryClient.getQueryData(queryKey) || [];
    const autoCharges = current.filter(p => p.lessonId && p.type === 'charge');
    const autoChargedIds = new Set(autoCharges.map(p => p.lessonId));

    const staleChargeIds = new Set(
      autoCharges.filter(p => knownIds.has(p.lessonId) && !doneIds.has(p.lessonId)).map(p => p.id)
    );
    const missing = items.filter(i => i.status === 'done' && !autoChargedIds.has(i.id));
    if (staleChargeIds.size === 0 && missing.length === 0) return;

    let next = current.filter(p => !staleChargeIds.has(p.id));
    let seq = 0;
    const additions = missing.map(item => {
      const amt = computeLessonCharge(item, priceAmount);
      return {
        id: Date.now() + (seq++),
        date: item.date || new Date().toISOString(),
        amount: -amt,
        type: 'charge',
        lessonId: item.id,
        note: `${item.title || 'Lesson'} (auto)`,
      };
    });
    next = [...next, ...additions];
    savePayments(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessons, scheduleItems, paymentsKey, ready, notSettled, priceAmount, queryKey]);

  const paymentBalance = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return {
    payments, savePayments, computeLessonCharge,
    paymentBalance, paymentCurrency, paymentsKey,
    // True only for useLocalCache:false while the real Supabase fetch is
    // still in flight — lets the caller show a loading state instead of
    // flashing a stale value. useLocalCache:true is never "loading" here;
    // it shows the local snapshot immediately, same as before.
    paymentsLoading: useLocalCache ? false : isLoading,
  };
}
